import React, { useState, useCallback, useEffect, useRef } from 'react';
import { format, isToday } from 'date-fns';
import { Clock } from 'lucide-react';
import TaskCard, { EFFORT_OPTIONS } from './TaskCard';
import { OrkaPane } from '../orka';

// Time configuration
const START_HOUR = 4;
const END_HOUR = 24;
const SLOT_HEIGHT = 30;
const SLOT_MINUTES = 15;

// Generate time slots
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = START_HOUR; hour < END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += SLOT_MINUTES) {
      slots.push({
        hour,
        minute,
        time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      });
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

// Z-index constants
const Z_INDEX_NOW_LINE = 30;
const Z_INDEX_ACTIVE_CARD = 500; // Much higher than everything else

const Timeline = ({
  tasks,
  selectedDate,
  borderColor,
  totalEffort,
  totalDuration,
  taskCount,
  onTaskUpdate,
  onTaskDelete,
  onDragDropChange,
  onCreateTask,
  draggingTask,
  onDragStart,
  onDragEnd,
  userTimezone = 'UTC',
}) => {
  const [ghostPosition, setGhostPosition] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeCardId, setActiveCardId] = useState(null);
  const [hoverLineY, setHoverLineY] = useState(null);
  const [hoverTime, setHoverTime] = useState(null);
  const [dragCreate, setDragCreate] = useState(null); // { startY, startTime, currentY, currentTime }
  const dragCreateJustEnded = useRef(false);
  const timelineContentRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const autoRefreshIntervalRef = useRef(null);

  // Handle card becoming active/inactive
  const handleCardActiveChange = useCallback((taskId, isActive) => {
    if (isActive) {
      setActiveCardId(taskId);
    } else if (activeCardId === taskId) {
      setActiveCardId(null);
    }
  }, [activeCardId]);

  // Update current time and handle auto-refresh after 5 mins of inactivity
  useEffect(() => {
    // Track user activity
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };
    
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    
    // Update time every minute
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    // Check for inactivity and refresh every 5 minutes
    autoRefreshIntervalRef.current = setInterval(() => {
      const now = Date.now();
      if (now - lastActivityRef.current >= 5 * 60 * 1000) {
        setCurrentTime(new Date());
      }
    }, 60000);
    
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      clearInterval(timeInterval);
      clearInterval(autoRefreshIntervalRef.current);
    };
  }, []);

  // Calculate current time line position
  const getNowLinePosition = () => {
    if (!isToday(selectedDate)) return null;
    
    const now = currentTime;
    const hours = now.getHours();
    const minutes = now.getMinutes();
    
    if (hours < START_HOUR || hours >= END_HOUR) return null;
    
    // Calculate position based on time slots
    const slotIndex = (hours - START_HOUR) * 4 + (minutes / 15);
    return slotIndex * SLOT_HEIGHT;
  };

  const nowLinePosition = getNowLinePosition();

  const hours = Math.floor(totalDuration / 60);
  const mins = totalDuration % 60;

  const getTaskPosition = (task) => {
    if (!task.task_local_start_time) return null;
    const [taskHours, taskMinutes] = task.task_local_start_time.split(':').map(Number);
    const slotIndex = (taskHours - START_HOUR) * 4 + Math.floor(taskMinutes / 15);
    
    let durationSlots = 4; // Default 1 hour
    if (task.task_local_end_time) {
      const [endHours, endMinutes] = task.task_local_end_time.split(':').map(Number);
      durationSlots = Math.ceil(((endHours * 60 + endMinutes) - (taskHours * 60 + taskMinutes)) / 15);
    } else if (task.estimated_minutes) {
      durationSlots = Math.ceil(task.estimated_minutes / 15);
    } else {
      const effortOpt = EFFORT_OPTIONS.find(e => e.value === task.task_estimated_effort);
      durationSlots = Math.ceil((effortOpt?.minutes || 30) / 15);
    }
    
    return { 
      top: slotIndex * SLOT_HEIGHT, 
      height: Math.max(durationSlots * SLOT_HEIGHT, SLOT_HEIGHT), 
      slotIndex,
      durationSlots
    };
  };

  // Calculate stacking for overlapping cards
  // Smaller duration cards stack on top, indented just enough to show grip cell (24px)
  const GRIP_WIDTH = 24;
  
  const getStackedTasks = () => {
    // Get positions for all tasks
    const tasksWithPos = tasks
      .map(t => ({ task: t, pos: getTaskPosition(t) }))
      .filter(({ pos }) => pos !== null);
    
    // Sort by start time, then by duration (larger first so smaller cards get higher z-index)
    tasksWithPos.sort((a, b) => {
      if (a.pos.top !== b.pos.top) return a.pos.top - b.pos.top;
      return b.pos.durationSlots - a.pos.durationSlots;
    });
    
    // Calculate stacking for each task
    const result = [];
    
    for (let i = 0; i < tasksWithPos.length; i++) {
      const current = tasksWithPos[i];
      const currentEnd = current.pos.top + current.pos.height;
      
      // Find overlapping tasks that come before this one
      let stackLevel = 0;
      for (let j = 0; j < i; j++) {
        const other = tasksWithPos[j];
        const otherEnd = other.pos.top + other.pos.height;
        
        // Check if overlapping
        if (current.pos.top < otherEnd && currentEnd > other.pos.top) {
          // They overlap - check if this one is smaller (should be on top)
          if (current.pos.durationSlots <= other.pos.durationSlots) {
            stackLevel = Math.max(stackLevel, (result[j]?.stackLevel || 0) + 1);
          }
        }
      }
      
      result.push({
        ...current,
        stackLevel,
        indent: stackLevel * GRIP_WIDTH,
        zIndex: 10 + stackLevel  // Base z-index + stack level
      });
    }
    
    return result;
  };

  const stackedTasks = getStackedTasks();

  const handleDragOver = useCallback((e, slotIndex) => {
    e.preventDefault();
    if (!draggingTask) return;
    const slot = timeSlots[slotIndex];
    if (!slot) return;
    
    // Calculate duration slots based on task
    let durationSlots = 4;
    if (draggingTask.start_time && draggingTask.end_time) {
      const [startH, startM] = draggingTask.start_time.split(':').map(Number);
      const [endH, endM] = draggingTask.end_time.split(':').map(Number);
      durationSlots = Math.ceil(((endH * 60 + endM) - (startH * 60 + startM)) / 15);
    } else if (draggingTask.estimated_minutes) {
      durationSlots = Math.ceil(draggingTask.estimated_minutes / 15);
    } else {
      const effortOpt = EFFORT_OPTIONS.find(e => e.value === draggingTask.task_estimated_effort);
      durationSlots = Math.ceil((effortOpt?.minutes || 30) / 15);
    }
    
    const maxSlotIndex = timeSlots.length - durationSlots;
    const clampedSlotIndex = Math.min(slotIndex, maxSlotIndex);
    
    setGhostPosition({ 
      top: clampedSlotIndex * SLOT_HEIGHT, 
      height: durationSlots * SLOT_HEIGHT, 
      time: timeSlots[clampedSlotIndex].time, 
      slotIndex: clampedSlotIndex 
    });
  }, [draggingTask]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (!draggingTask || !ghostPosition) {
      setGhostPosition(null);
      return;
    }

    const slot = timeSlots[ghostPosition.slotIndex];
    if (!slot) {
      setGhostPosition(null);
      return;
    }

    // Calculate duration
    let durationMinutes = 60;
    if (draggingTask.start_time && draggingTask.end_time) {
      const [startH, startM] = draggingTask.start_time.split(':').map(Number);
      const [endH, endM] = draggingTask.end_time.split(':').map(Number);
      durationMinutes = (endH * 60 + endM) - (startH * 60 + startM);
    } else if (draggingTask.estimated_minutes) {
      durationMinutes = draggingTask.estimated_minutes;
    } else {
      const effortOpt = EFFORT_OPTIONS.find(e => e.value === draggingTask.task_estimated_effort);
      durationMinutes = effortOpt?.minutes || 60;
    }

    const startMinutes = slot.hour * 60 + slot.minute;
    const endMinutes = Math.min(startMinutes + durationMinutes, END_HOUR * 60);
    const endHour = Math.floor(endMinutes / 60);
    const endMinute = endMinutes % 60;
    
    const newStartTime = slot.time;
    const newEndTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;

    setGhostPosition(null);
    onDragEnd();
    
    // Stage the change instead of saving immediately
    if (onDragDropChange) {
      onDragDropChange(draggingTask, newStartTime, newEndTime);
    }
  }, [draggingTask, ghostPosition, onDragDropChange, onDragEnd]);

  const handleDragLeave = () => {
    setGhostPosition(null);
  };

  // Hover-to-create handlers
  const handleTimelineMouseMove = useCallback((e) => {
    if (draggingTask || activeCardId) return;
    const container = timelineContentRef.current;
    if (!container) return;
    
    // Check if we're hovering over a task card
    const targetEl = e.target;
    if (targetEl.closest('[data-task-card]')) {
      setHoverLineY(null);
      setHoverTime(null);
      return;
    }
    
    const scrollTop = container.scrollTop;
    const rect = container.getBoundingClientRect();
    const relativeY = e.clientY - rect.top + scrollTop;
    
    // Snap to 15-minute slots
    const slotIndex = Math.floor(relativeY / SLOT_HEIGHT);
    if (slotIndex < 0 || slotIndex >= timeSlots.length) {
      setHoverLineY(null);
      setHoverTime(null);
      return;
    }
    
    const snappedY = slotIndex * SLOT_HEIGHT;
    setHoverLineY(snappedY);
    setHoverTime(timeSlots[slotIndex]?.time || null);
  }, [draggingTask, activeCardId]);

  const handleTimelineMouseLeave = useCallback(() => {
    setHoverLineY(null);
    setHoverTime(null);
  }, []);

  const handleTimelineClick = useCallback((e) => {
    if (draggingTask || activeCardId) return;
    if (e.target.closest('[data-task-card]')) return;
    if (e.target.closest('button')) return;
    // Don't fire click if a drag-create just completed (mouseup already handled it)
    if (dragCreateJustEnded.current) return;
    
    if (!hoverTime || !onCreateTask) return;
    onCreateTask(hoverTime);
  }, [draggingTask, activeCardId, hoverTime, onCreateTask]);

  // Drag-to-create: mousedown starts, mousemove extends, mouseup creates
  const handleDragCreateStart = useCallback((e) => {
    if (draggingTask || activeCardId) return;
    if (e.target.closest('[data-task-card]') || e.target.closest('button')) return;
    if (e.button !== 0) return; // left click only
    
    const container = timelineContentRef.current;
    if (!container) return;
    const scrollTop = container.scrollTop;
    const rect = container.getBoundingClientRect();
    const relativeY = e.clientY - rect.top + scrollTop;
    const slotIndex = Math.floor(relativeY / SLOT_HEIGHT);
    if (slotIndex < 0 || slotIndex >= timeSlots.length) return;
    
    setDragCreate({
      startSlot: slotIndex,
      startTime: timeSlots[slotIndex].time,
      currentSlot: slotIndex,
      currentTime: timeSlots[slotIndex].time
    });
  }, [draggingTask, activeCardId]);

  const handleDragCreateMove = useCallback((e) => {
    if (!dragCreate) return;
    const container = timelineContentRef.current;
    if (!container) return;
    const scrollTop = container.scrollTop;
    const rect = container.getBoundingClientRect();
    const relativeY = e.clientY - rect.top + scrollTop;
    const slotIndex = Math.max(dragCreate.startSlot, Math.min(Math.floor(relativeY / SLOT_HEIGHT), timeSlots.length - 1));
    setDragCreate(prev => ({
      ...prev,
      currentSlot: slotIndex,
      currentTime: timeSlots[slotIndex]?.time || prev.currentTime
    }));
  }, [dragCreate]);

  const handleDragCreateEnd = useCallback(() => {
    if (!dragCreate || !onCreateTask) { setDragCreate(null); return; }
    const { startSlot, currentSlot } = dragCreate;
    const minSlot = Math.min(startSlot, currentSlot);
    const maxSlot = Math.max(startSlot, currentSlot);
    const durationSlots = maxSlot - minSlot + 1;
    const durationMins = durationSlots * SLOT_MINUTES;
    
    const startTime = timeSlots[minSlot].time;
    // End time = start of the slot AFTER the last selected slot
    const endSlotIdx = Math.min(maxSlot + 1, timeSlots.length - 1);
    const endTime = timeSlots[endSlotIdx]?.time || timeSlots[timeSlots.length - 1].time;
    
    // Round up duration to nearest Fibonacci effort score
    // Effort: 1=15m, 2=30m, 3=60m, 5=180m, 8=360m, 13=600m
    let effort;
    if (durationMins <= 15) effort = 1;
    else if (durationMins <= 30) effort = 2;
    else if (durationMins <= 60) effort = 3;
    else if (durationMins <= 180) effort = 5;
    else if (durationMins <= 360) effort = 8;
    else effort = 13;
    
    setDragCreate(null);
    dragCreateJustEnded.current = true;
    setTimeout(() => { dragCreateJustEnded.current = false; }, 100);
    onCreateTask(startTime, endTime, effort);
  }, [dragCreate, onCreateTask]);

  // Listen for mouseup globally when drag-creating
  useEffect(() => {
    if (!dragCreate) return;
    const handleUp = () => handleDragCreateEnd();
    window.addEventListener('mouseup', handleUp);
    return () => window.removeEventListener('mouseup', handleUp);
  }, [dragCreate, handleDragCreateEnd]);

  return (
    <OrkaPane
      title="Day Planner"
      intent="focus"
      icon={Clock}
      borderTone={borderColor}
      stats={{
        effort: totalEffort,
        duration: `${hours}h ${mins}m`,
      }}
      rightSlot={
        <span className="text-xs orka-text-tertiary font-mono">{taskCount} task{taskCount !== 1 ? 's' : ''}</span>
      }
      bodyClassName="cursor-crosshair select-none"
      testId="timeline-pane"
    >
      {/* Timeline Content (grid + now-line + drag-create — preserved verbatim) */}
      <div
        ref={timelineContentRef}
        className="-m-3 p-3"
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={handleDragLeave}
        onMouseMove={(e) => { handleTimelineMouseMove(e); handleDragCreateMove(e); }}
        onMouseLeave={handleTimelineMouseLeave}
        onClick={handleTimelineClick}
        onMouseDown={handleDragCreateStart}
      >
        <div 
          className="relative" 
          style={{ height: (END_HOUR - START_HOUR) * 4 * SLOT_HEIGHT }}
        >
          {/* Time slots */}
          {timeSlots.map((slot, index) => (
            <div
              key={`slot-${index}`}
              className={`absolute left-0 right-0 flex border-b border-stone-100 dark:border-slate-700 ${
                ghostPosition?.slotIndex === index ? 'bg-indigo-50/50' : ''
              }`}
              style={{ top: index * SLOT_HEIGHT, height: SLOT_HEIGHT }}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={handleDrop}
            >
              {slot.minute === 0 && (
                <div className="w-14 flex-shrink-0 pr-2 text-right flex items-center justify-end">
                  <span className="text-xs font-mono orka-text-tertiary">
                    {format(new Date().setHours(slot.hour, 0), 'h a')}
                  </span>
                </div>
              )}
              {slot.minute !== 0 && <div className="w-14 flex-shrink-0" />}
              <div className="flex-1 border-l orka-border-subtle" />
            </div>
          ))}

          {/* Current Time Red Line - only shown for today */}
          {nowLinePosition !== null && (
            <div 
              className="absolute left-0 right-0 pointer-events-none flex items-center"
              style={{ top: nowLinePosition, zIndex: Z_INDEX_NOW_LINE }}
            >
              <div className="w-14 flex-shrink-0 pr-2 text-right">
                <span className="text-[10px] font-bold orka-text-danger">
                  {format(currentTime, 'h:mm')}
                </span>
              </div>
              <div className="flex-1 h-[2px] orka-bg-danger shadow-sm" />
              <div className="w-2 h-2 rounded-full orka-bg-danger -ml-1" />
            </div>
          )}

          {/* Tasks - with stacking logic for overlapping cards */}
          {stackedTasks.map(({ task, pos, stackLevel, indent, zIndex }) => {
            // Active card gets highest z-index, above everything including now line
            const isThisCardActive = activeCardId === task.task_id;
            const finalZIndex = isThisCardActive ? Z_INDEX_ACTIVE_CARD : zIndex;
            
            return (
              <div
                key={task.task_id}
                className="absolute right-2"
                data-task-card="true"
                style={{ 
                  top: pos.top + 2, 
                  height: pos.height - 4,
                  left: 56 + indent, // 56px base (14 * 4) + indent for stacking
                  zIndex: finalZIndex,
                  overflow: 'visible' // Allow expanded cards to overflow
                }}
              >
                <TaskCard
                  task={task}
                  context="timeline"
                  fixedHeight={pos.height - 4}
                  onUpdate={onTaskUpdate}
                  onDelete={onTaskDelete}
                  isDragging={draggingTask?.task_id === task.task_id}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  stackIndex={stackLevel}
                  onActiveChange={(isActive) => handleCardActiveChange(task.task_id, isActive)}
                />
              </div>
            );
          })}

          {/* Hover Line Indicator for click-to-create */}
          {hoverLineY !== null && !draggingTask && (
            <div
              className="absolute left-14 right-2 pointer-events-none flex items-center"
              style={{ top: hoverLineY, zIndex: 5 }}
              data-testid="timeline-hover-line"
            >
              <div className="flex-1 h-[2px] bg-indigo-300/60" />
              {hoverTime && (
                <span className="text-[10px] font-mono text-indigo-400 ml-1 bg-white/80 px-1 rounded">
                  {hoverTime}
                </span>
              )}
            </div>
          )}

          {/* Ghost Indicator */}
          {ghostPosition && (
            <div 
              className="absolute left-14 right-2 pointer-events-none z-40 orka-bg-intent-soft border-2 border-dashed border-indigo-400 rounded-lg flex items-center justify-center"
              style={{ 
                top: ghostPosition.top, 
                height: ghostPosition.height - 2,
                transition: 'top 75ms ease-out'
              }}
            >
              <span className="text-xs font-mono orka-text-intent font-medium">
                {ghostPosition.time}
              </span>
            </div>
          )}

          {/* Drag-to-create indicator */}
          {dragCreate && (() => {
            const minSlot = Math.min(dragCreate.startSlot, dragCreate.currentSlot);
            const maxSlot = Math.max(dragCreate.startSlot, dragCreate.currentSlot);
            const durationSlots = maxSlot - minSlot + 1;
            const durationMins = durationSlots * SLOT_MINUTES;
            return (
              <div
                className="absolute left-14 right-2 pointer-events-none z-40 rounded-lg orka-bg-success-soft border-2 border-emerald-400 flex items-center justify-center"
                style={{
                  top: minSlot * SLOT_HEIGHT,
                  height: durationSlots * SLOT_HEIGHT - 2,
                }}
              >
                <span className="text-xs font-mono orka-text-success font-medium">
                  {timeSlots[minSlot]?.time} — {timeSlots[maxSlot + 1]?.time || timeSlots[maxSlot]?.time} ({durationMins}m)
                </span>
              </div>
            );
          })()}
        </div>
      </div>
    </OrkaPane>
  );
};

export default Timeline;
